import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FiHeart, FiMenu, FiSearch, FiShoppingBag, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import BrandLogo from "./BrandLogo";
import MoreMenu from "./MoreMenu";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const closeWithEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", closeWithEscape);
    return () => window.removeEventListener("keydown", closeWithEscape);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchValue.trim();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
    scrollTop();
  };

  return (
    <>
      <header className="site-header">
        <div className="site-header__bar">
          <button
            className="site-header__menu-button"
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <FiMenu aria-hidden="true" />
            <span>Menu</span>
          </button>

          <nav className="site-header__nav" aria-label="Primary navigation">
            <NavLink to="/" end onClick={scrollTop}>
              Home
            </NavLink>
            <NavLink to="/shop" onClick={scrollTop}>
              Collectie
            </NavLink>
            <NavLink to="/about" onClick={scrollTop}>
              Over ons
            </NavLink>
            <NavLink to="/contact" onClick={scrollTop}>
              Contact
            </NavLink>
            <MoreMenu />
          </nav>

          <Link
            className="site-header__brand"
            to="/"
            onClick={scrollTop}
            aria-label={`${siteConfig.displayName} home`}
          >
            <BrandLogo />
          </Link>

          <div className="site-header__actions">
            <span className="site-header__language" aria-label="Current language Dutch">
              NL
            </span>
            <button
              type="button"
              onClick={() => setSearchOpen((open) => !open)}
              aria-label="Search collection"
            >
              <FiSearch aria-hidden="true" />
            </button>
            <Link
              to="/shop?wishlist=1"
              aria-label={`Wishlist with ${wishlist.length} items`}
              onClick={scrollTop}
            >
              <FiHeart aria-hidden="true" />
              {wishlist.length > 0 && <span>{wishlist.length}</span>}
            </Link>
            <Link
              to="/cartpage"
              aria-label={`Shopping bag with ${cartCount} items`}
              onClick={scrollTop}
            >
              <FiShoppingBag aria-hidden="true" />
              <span>{cartCount}</span>
            </Link>
          </div>
        </div>

        {searchOpen && (
          <form className="site-header__search" onSubmit={submitSearch}>
            <label htmlFor="site-search">Waar ben je naar op zoek?</label>
            <input
              ref={searchInput}
              id="site-search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Zoek op model, kleur of fit"
            />
            <button type="submit">Zoeken</button>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
            >
              <FiX aria-hidden="true" />
            </button>
          </form>
        )}
      </header>

      <div className={`site-menu ${menuOpen ? "site-menu--open" : ""}`} inert={menuOpen ? undefined : ""} aria-hidden={!menuOpen}>
        <button
          className="site-menu__close"
          type="button"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <FiX aria-hidden="true" />
        </button>
        <div className="site-menu__eyebrow">Discover {siteConfig.displayName}</div>
        <nav aria-label="Mobile navigation">
          <NavLink to="/" end onClick={scrollTop}>
            Home
          </NavLink>
          <NavLink to="/shop" onClick={scrollTop}>
            Collectie
          </NavLink>
          <NavLink to="/about" onClick={scrollTop}>
            Over ons
          </NavLink>
          <NavLink to="/contact" onClick={scrollTop}>
            Contact
          </NavLink>
          <MoreMenu mobile />
        </nav>
        <div className="site-menu__utility">
          <Link to="/shipping" onClick={scrollTop}>Verzending</Link>
          <Link to="/returns" onClick={scrollTop}>Retourneren</Link>
          <Link to="/privacy" onClick={scrollTop}>Privacy</Link>
        </div>
      </div>
      {menuOpen && (
        <button
          className="site-menu__backdrop"
          type="button"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        />
      )}
    </>
  );
}

export default Header;
